import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useCurrentCompany, useCurrentUser, useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { useNavbarPanelsStore } from '../../../hooks/use-navbar-panels';
import { usePrefetchBizyPromptSuggestions } from '../../../queries/bizy';
import { useHasPendingTrialWelcomeDemo, useIsTrialWelcomeDemoResolved } from '../../onboarding/use-has-pending-trial-welcome-demo';
import { useHasPendingMeetBizyPromo, useIsMeetBizyPromoResolved } from '../../promo/use-has-pending-meet-bizy-promo';
import { BIZY_ONBOARDING_PATH } from '../constants';
import { resolvePageContext } from '../lib/bizy-page-config';
import {
	getBizyPanelPreference,
	hasTriggeredTrialAutoOpen,
	markTrialAutoOpenTriggered,
	setBizyPanelPreference,
	shouldAutoOpenBizyPanel,
} from '../lib/panel-preferences';
import { getAudienceSegment, isTrialSegment } from '../lib/suggested-prompts';
import { useBizyPageContextStore } from '../store/use-bizy-page-context-store';
import { BIZY_DEEP_LINK_TRIGGER, BIZY_ROUTE_TRIGGER, useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useBizyDeepLink } from './use-bizy-deep-link';
import { useBizyMomentsDeepLink } from './use-bizy-moments-deep-link';
import { useBizyPanelTracking } from './use-bizy-panel-tracking';
import { useBizyRouteSync } from './use-bizy-route-sync';
import { useBizySettingsDeepLink } from './use-bizy-settings-deep-link';

interface UseBizyLayoutOptions {
	isMobile: boolean;
	isExpanded: boolean;
	setIsExpanded: (expanded: boolean) => void;
}

// Triggers whose fullscreen the drop-to-side-panel effects must not undo: a
// `?bizy=fullscreen` URL open, whether it came through the legacy deep-link hook
// (BIZY_DEEP_LINK_TRIGGER) or the `?bizy=` route sync (BIZY_ROUTE_TRIGGER, which
// owns the param since BIZY-680). Without the route trigger, a pathname change
// right after a fullscreen URL open would drop the fullscreen it just requested.
// User-toggled fullscreen still drops on cross-page nav, as before.
const FULLSCREEN_PROTECTED_TRIGGERS = new Set([BIZY_DEEP_LINK_TRIGGER, BIZY_ROUTE_TRIGGER]);

export function handleBizySlashKeyDown(event: KeyboardEvent, togglePanel: () => void) {
	if (event.key !== '/' || event.repeat || event.metaKey || event.ctrlKey) return;

	const target = event.target as HTMLElement | null;
	const tag = target?.tagName;
	const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable;
	if (isEditable) return;

	event.preventDefault();
	togglePanel();
}

/**
 * Encapsulates all Bizy side-panel layout concerns so AppLayout stays clean.
 *
 * Handles: company gating, store subscriptions, route-based page context,
 * homepage auto-open, sidebar auto-collapse on medium screens,
 * and Bizy auto-collapse when navbar panels open.
 */
export function useBizyLayout({ isMobile, isExpanded, setIsExpanded }: UseBizyLayoutOptions) {
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	usePrefetchBizyPromptSuggestions(isBizyEnabledForUser && !isMobile);
	const hasPendingMeetBizyPromo = useHasPendingMeetBizyPromo();
	const hasPendingTrialWelcomeDemo = useHasPendingTrialWelcomeDemo();
	// Read unconditionally, never behind `&&`, so the hook order stays stable.
	const isMeetBizyPromoResolved = useIsMeetBizyPromoResolved();
	const isTrialWelcomeDemoResolved = useIsTrialWelcomeDemoResolved();
	const location = useLocation();
	const company = useCurrentCompany();
	const currentUser = useCurrentUser();

	const isPanelOpen = useBizySidePanelStore((s) => s.isOpen);
	const isFullscreen = useBizySidePanelStore((s) => s.isFullscreen);
	const lastTrigger = useBizySidePanelStore((s) => s.lastTrigger);
	const panelWidth = useBizySidePanelStore((s) => s.width);
	const isResizing = useBizySidePanelStore((s) => s.isResizing);
	const togglePanel = useBizySidePanelStore((s) => s.toggle);
	const openPanel = useBizySidePanelStore((s) => s.open);
	const closePanel = useBizySidePanelStore((s) => s.close);
	const setPageContext = useBizyPageContextStore((s) => s.setContext);

	const isDesktop = isBizyEnabledForUser && !isMobile;
	const restoreOpenPanel = useCallback(() => openPanel('auto', { isHistoryDrawerOpen: false }), [openPanel]);

	// `?bizy_settings=<section>` opens the panel at that settings section (BIZY-213).
	useBizySettingsDeepLink();
	// Memoize by pathname so the object identity is stable across renders.
	// Without this, `resolvePageContext` returns a fresh object each render,
	// which makes the sync effect below re-fire on every render and overwrite
	// any richer pageData set imperatively by a mounted page component (e.g.
	// OneOnOneView adds partner name and is_first_meeting to the store).
	const pageContext = useMemo(() => resolvePageContext(location.pathname), [location.pathname]);
	const hasAppliedInitialPrefRef = useRef(false);

	const { trackPanelAutoOpened } = useBizyPanelTracking(isPanelOpen);

	const [isLargeDesktop, setIsLargeDesktop] = useState(() => {
		if (typeof window === 'undefined') return false;
		return window.matchMedia('(min-width: 1280px)').matches;
	});

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const media = window.matchMedia('(min-width: 1280px)');
		const listener = (event: MediaQueryListEvent) => setIsLargeDesktop(event.matches);
		setIsLargeDesktop(media.matches);
		media.addEventListener('change', listener);

		return () => media.removeEventListener('change', listener);
	}, []);

	// Sync page context from current route
	useEffect(() => {
		if (!isBizyEnabledForUser || isMobile) return;
		setPageContext(pageContext);
	}, [isBizyEnabledForUser, isMobile, setPageContext, pageContext]);

	// Drop to side panel on cross-layout mount when fullscreen (e.g. arriving from admin).
	// On page refresh the store starts with isFullscreen: false, so this is a no-op.
	// Uses setState directly to preserve the user's fullscreen preference in localStorage.
	// Skips a `?bizy=fullscreen` URL open (see FULLSCREEN_PROTECTED_TRIGGERS) so a
	// StrictMode re-run or post-open route change can't drop the fullscreen it just requested.
	useEffect(() => {
		const state = useBizySidePanelStore.getState();
		if (state.isFullscreen && !FULLSCREEN_PROTECTED_TRIGGERS.has(state.lastTrigger ?? '')) {
			useBizySidePanelStore.setState({ isFullscreen: false, isHistoryDrawerOpen: false });
		}
	}, []);

	// Drop to side panel on within-layout route change when fullscreen.
	// Preserves the user's fullscreen preference so reopening restores fullscreen.
	const prevPathnameRef = useRef(location.pathname);
	useEffect(() => {
		const prev = prevPathnameRef.current;
		prevPathnameRef.current = location.pathname;
		const state = useBizySidePanelStore.getState();
		if (prev !== location.pathname && state.isFullscreen && !FULLSCREEN_PROTECTED_TRIGGERS.has(state.lastTrigger ?? '')) {
			useBizySidePanelStore.setState({ isFullscreen: false, isHistoryDrawerOpen: false });
		}
	}, [location.pathname]);

	// Keep the Bizy bar closed while the first-run Meet Bizy promo is pending.
	useEffect(() => {
		if (!isBizyEnabledForUser || isMobile) return;
		if (!hasPendingMeetBizyPromo) return;
		if (pageContext.pageType !== 'home') return;
		closePanel('auto');
	}, [closePanel, hasPendingMeetBizyPromo, isBizyEnabledForUser, isMobile, pageContext.pageType]);

	// Auto-open once per app load, from the saved preference.
	//
	// Navigation neither opens nor closes the panel. Bizy is either there because the
	// member left it there, or it isn't there until they ask for it — no route is
	// special. This used to be route-aware: arriving at a 1:1 could open the panel and
	// arriving anywhere else could close it, driven by two page-type preferences that
	// had no UI and that nobody knowingly set (closing the panel once on a 1:1 silently
	// meant "never show it on 1:1s"). Losing an open conversation on a page change is a
	// bad trade for a default nobody asked for, so the whole route-aware branch is gone
	// and the single preference now only ever decides whether to open on load.
	useEffect(() => {
		if (!isBizyEnabledForUser || isMobile) return;

		// The first-run Meet Bizy promo and the trial welcome demo take over the screen
		// and drive the panel themselves, so neither may be pending. Until their
		// dismissal records land there is no answer — the state of EVERY fresh page
		// load — and we return without spending the once-per-load flag below. Latching
		// on the unknown would mean a refresh never restores the panel for anyone,
		// because the first render always looks like a pending takeover.
		if (!isMeetBizyPromoResolved || !isTrialWelcomeDemoResolved) return;

		if (hasAppliedInitialPrefRef.current) return;
		hasAppliedInitialPrefRef.current = true;

		// Spend the flag whichever way the answer went, because "pending" is not
		// stable for the life of the load: advancing an onboarding card writes
		// `onboardingStage`, and the promo's claim is keyed on stage 0, so it flips
		// false with the modal still on screen. Latching below that flip instead would
		// read it as "no takeover" and slide the panel open behind the takeover, where
		// it would still be waiting once the member finished.
		if (hasPendingMeetBizyPromo || hasPendingTrialWelcomeDemo) return;

		// A load that STARTS on the takeover's own route needs the same treatment, and
		// pendingness won't tell us: the modal renders on that route whatever the stage
		// or dismissal says (which is how the settings replay reopens it), while the
		// takeover reports "not pending" as soon as the stage is past 0 or the record is
		// dismissed. So a refresh mid-flow, or a connector's OAuth round-trip landing
		// back here, looks exactly like "no takeover" — and the panel would open behind
		// a modal that has already run its own close.
		if (location.pathname === BIZY_ONBOARDING_PATH) return;

		// Only on large desktops, where there is room for the panel beside the page.
		if (!isLargeDesktop) return;
		if (!shouldAutoOpenBizyPanel()) return;
		if (getBizyPanelPreference() !== 'open') return;
		if (useBizySidePanelStore.getState().isOpen) return;

		restoreOpenPanel();
	}, [
		hasPendingMeetBizyPromo,
		hasPendingTrialWelcomeDemo,
		isMeetBizyPromoResolved,
		isTrialWelcomeDemoResolved,
		isBizyEnabledForUser,
		isMobile,
		isLargeDesktop,
		location.pathname,
		restoreOpenPanel,
	]);

	// Trial users landing on the homepage get the Bizy panel auto-opened once per
	// browser session, so the first thing they see is a guided entry point.
	// Desktop only; the sessionStorage flag is keyed per user to keep the trigger
	// stable across user switches in the same tab.
	const userId = currentUser?.id;
	// Derive trial status from the same audience-segment helper the chat view
	// uses, so the auto-open trigger and the trial welcome content can never
	// disagree about whether this is a trial company.
	const isTrial = isTrialSegment(getAudienceSegment(company?.status ?? '', currentUser?.admin ?? false));
	const isHomepage = pageContext.pageType === 'home';
	useEffect(() => {
		if (!userId) return;
		if (!isTrial) return;
		if (!isDesktop) return;
		if (!isHomepage) return;
		if (hasPendingMeetBizyPromo) return;
		// Don't spend the trigger here — auto-launch spends it when the demo opens.
		if (hasPendingTrialWelcomeDemo) return;
		if (!shouldAutoOpenBizyPanel()) return;
		if (hasTriggeredTrialAutoOpen(userId)) return;

		// Mark the trigger as spent before the isOpen guard: if another effect
		// (e.g. the general-preference auto-open) already opened the panel this
		// render, the trial trigger is still logically "used" for this session.
		// Otherwise the flag would never persist and a later user-close +
		// navigation back to home would re-trigger auto-open, overriding intent.
		markTrialAutoOpenTriggered(userId);

		// Always fire the explicit auto-open analytics event when the trial
		// trigger fires this session — even if another effect already opened
		// the panel — so we can measure trial auto-open attribution distinct
		// from the generic ChatOpened event.
		trackPanelAutoOpened();

		if (useBizySidePanelStore.getState().isOpen) return;

		useBizySidePanelStore.getState().open('auto', { isFullscreen: false, isHistoryDrawerOpen: false });
	}, [userId, isTrial, isDesktop, isHomepage, hasPendingMeetBizyPromo, hasPendingTrialWelcomeDemo, trackPanelAutoOpened]);

	// Save preference when user manually closes or opens the panel
	const prevIsOpenRef = useRef(isPanelOpen);
	useEffect(() => {
		const wasOpen = prevIsOpenRef.current;
		prevIsOpenRef.current = isPanelOpen;
		if (wasOpen === isPanelOpen) return;
		// Persist for explicit user actions and notification-peek opens.
		// Including notification_peek means a peek-open survives a page
		// refresh (the user expects to find the panel where they left it).
		// The downside — peek-clicking a single notif may flip a previously
		// "closed" preference to "open" — is acceptable; users who never
		// want the panel can always close it again.
		if (lastTrigger !== 'user' && lastTrigger !== 'keyboard' && lastTrigger !== 'notification_peek') return;

		setBizyPanelPreference(isPanelOpen ? 'open' : 'closed');
	}, [isPanelOpen, lastTrigger]);

	// Collapse the sidebar when Bizy needs the extra horizontal space. Restore
	// it only if Bizy collapsed an expanded sidebar in this session.
	const wasExpandedBeforeBizyRef = useRef<boolean | null>(null);
	const isSidebarCollapsedForBizyRef = useRef(false);
	// Whether the current collapse was caused by fullscreen specifically (vs.
	// the medium-screen case), so restore gets the same transition treatment.
	const collapsedForFullscreenRef = useRef(false);
	// One-shot: makes the sidebar snap instead of animate when fullscreen
	// causes the collapse/restore, matching the panel's own instant snap.
	const [suppressSidebarTransition, setSuppressSidebarTransition] = useState(false);
	useEffect(() => {
		if (!suppressSidebarTransition) return;
		setSuppressSidebarTransition(false);
	}, [suppressSidebarTransition]);
	useEffect(() => {
		if (isMobile) return;

		const shouldCollapseSidebar = isPanelOpen && (isFullscreen || !isLargeDesktop);

		if (shouldCollapseSidebar && !isSidebarCollapsedForBizyRef.current) {
			isSidebarCollapsedForBizyRef.current = true;
			collapsedForFullscreenRef.current = isFullscreen;
			wasExpandedBeforeBizyRef.current = isExpanded;
			if (isExpanded) {
				if (isFullscreen) setSuppressSidebarTransition(true);
				setIsExpanded(false);
			}
			return;
		}

		if (!shouldCollapseSidebar && isSidebarCollapsedForBizyRef.current) {
			isSidebarCollapsedForBizyRef.current = false;
			if (wasExpandedBeforeBizyRef.current) {
				if (collapsedForFullscreenRef.current) setSuppressSidebarTransition(true);
				setIsExpanded(true);
			}
			wasExpandedBeforeBizyRef.current = null;
		}
	}, [isPanelOpen, isFullscreen, isLargeDesktop, isMobile, isExpanded, setIsExpanded]);

	// Auto-collapse Bizy when a navbar panel (Leaderboard, Team, etc.) opens; restore when it closes
	const openNavbarPanel = useNavbarPanelsStore((s) => s.openPanel);
	const wasBizyOpenBeforeSheetRef = useRef(false);
	useEffect(() => {
		if (isMobile) return;
		if (openNavbarPanel) {
			if (useBizySidePanelStore.getState().isOpen) {
				wasBizyOpenBeforeSheetRef.current = true;
				useBizySidePanelStore.getState().close('navbar_collapse');
			}
		} else if (wasBizyOpenBeforeSheetRef.current) {
			wasBizyOpenBeforeSheetRef.current = false;
			useBizySidePanelStore.getState().open('navbar_restore');
		}
	}, [openNavbarPanel, isMobile]);

	// "/" toggles the Bizy panel (when not focused in an input/textarea/contenteditable)
	const handleKeyDown = useCallback((event: KeyboardEvent) => handleBizySlashKeyDown(event, () => togglePanel('keyboard')), [togglePanel]);

	useEffect(() => {
		if (!isBizyEnabledForUser || isMobile) return;
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isBizyEnabledForUser, isMobile, handleKeyDown]);

	// Two-way sync of the panel with the `?bizy=` search param (BIZY-680). Member
	// web app only (desktop); mobile Bizy and the Rails-admin mount stay overlay-only.
	useBizyRouteSync(isDesktop);

	// Companion to route sync for bare `?bizy=noticed` (first People Browser card).
	// Before useBizyDeepLink so showChat cannot clear the selected person.
	useBizyMomentsDeepLink();

	// One-shot deep-link params (`bizy_prompt`, `bizy_moment`, …). Mount LAST so
	// its open wins over the mount-time auto-open/close and drop-to-side-panel.
	useBizyDeepLink();

	return {
		isDesktop,
		isPanelOpen,
		isFullscreen,
		panelWidth,
		isResizing,
		togglePanel,
		suppressSidebarTransition,
	};
}
